import { withTranslation } from 'react-i18next';

import DataTable from '../../../../components/Datatable';
import { getSortKeys, setAdditionalStyles } from '../../../../helpers/table';
import { LONG_TAIL_MIN_WORDS, MODE, PRESET, VIEW } from '../scope';
import { RULE_COLUMNS } from '../keys';

/**
 * Block 01 — what the report on screen was actually read under.
 *
 * It states the applied scope rather than offering another place to change it: the rules as
 * Search Console received them, and whether the local preset did anything. Rules sent to the API
 * and presets applied in the browser are listed apart on purpose — one narrows what was
 * retrieved, the other only narrows what is shown, and a reader who confuses the two will
 * misread every count below.
 */
function AppliedFilters({ t, scope }) {
    const columns = RULE_COLUMNS(t);

    const rows = scope.rules.map((rule, index) => ({
        id: `${rule.dimension}-${rule.operator}-${rule.expression}-${index}`,
        name: rule.name,
        dimension: rule.dimension,
        operator: rule.operator,
        expression: rule.expression,
    }));

    const presetLine = () => {
        if (scope.view !== VIEW.QUERIES) {
            return t('tools.pages_queries_changes.applied.preset_pages', {
                preset: t(`tools.pages_queries_changes.presets.${scope.preset}`),
            });
        }
        if (scope.preset === PRESET.ALL) {
            return t('tools.pages_queries_changes.applied.preset_none');
        }
        if (scope.preset === PRESET.QUESTIONS) {
            return t('tools.pages_queries_changes.applied.preset_questions');
        }
        return t('tools.pages_queries_changes.applied.preset_long_tail', {
            words: LONG_TAIL_MIN_WORDS,
        });
    };

    return (
        <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
            <div className="card-header d-flex justify-content-between">
                <div>
                    <p className="card-title">{t('tools.pages_queries_changes.applied.title')}</p>
                    <p className="gsc-tool-sub">
                        {t('tools.pages_queries_changes.applied.description')}
                    </p>
                </div>
            </div>

            <div className="card-body">
                <dl className="gsc-tool-summary">
                    <div>
                        <dt>{t('tools.pages_queries_changes.controls.view')}</dt>
                        <dd>{t(`tools.pages_queries_changes.views.${scope.view}`)}</dd>
                    </div>
                    <div>
                        <dt>{t('tools.pages_queries_changes.controls.mode')}</dt>
                        <dd>
                            {t(`tools.pages_queries_changes.modes.${scope.mode}`)}
                            {' · '}
                            {t(`tools.gsc.metrics.${scope.metric}`)}
                            {scope.mode !== MODE.POPULAR && (
                                <small className="d-block text-muted">
                                    {t('tools.pages_queries_changes.applied.mode_comparable')}
                                </small>
                            )}
                        </dd>
                    </div>
                    <div>
                        <dt>{t('tools.pages_queries_changes.controls.preset')}</dt>
                        <dd>{presetLine()}</dd>
                    </div>
                </dl>

                {!rows.length ? (
                    <p className="gsc-tool-table-note">
                        {t('tools.pages_queries_changes.applied.no_rules')}
                    </p>
                ) : (
                    <>
                        <DataTable
                            nodes={rows}
                            COLUMNS={columns}
                            sortFns={getSortKeys(columns)}
                            additionalStyles={setAdditionalStyles(columns)}
                            onlyTable
                            notUseDefaultStyle
                            customContainerStyles="gsc-tool-rules-table"
                        />
                        <p className="gsc-tool-table-note">
                            {t('tools.pages_queries_changes.applied.and_only')}
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}

export default withTranslation()(AppliedFilters);
